import React from 'react';
import { View, Text, TouchableOpacity, TextInput, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { WorkspaceQuotationPreview } from '../services/workspace-assistant.types';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing } from '../theme';

/** Per requested line: the product the user keeps (one of the offered ones) and the quantity; null = left out. */
export type QuotationChoice = { productId: string; quantity: number } | null;

export const initialQuotationChoices = (q: WorkspaceQuotationPreview): QuotationChoice[] =>
  q.lines.map(l => (l.selected ? { productId: l.selected.productId, quantity: l.quantity } : null));

/**
 * A prepared quotation to review before it is created: each line as the customer asked for it, the
 * product matched (switchable to the closest alternatives), its quantity and line total.
 */
export function WorkspaceQuotationReview({ quotation, choices, editable, onChange }: {
  quotation: WorkspaceQuotationPreview; choices: QuotationChoice[]; editable: boolean; onChange: (choices: QuotationChoice[]) => void;
}) {
  const { t, isRTL, locale } = useLocale(), styles = useThemedStyles(getStyles);
  const align = { textAlign: isRTL ? 'right' as const : 'left' as const }, row = { flexDirection: isRTL ? 'row-reverse' as const : 'row' as const };
  const money = (n: number) => { try { return n.toLocaleString(locale, { maximumFractionDigits: 2 }); } catch { return String(n); } };
  const set = (i: number, value: QuotationChoice) => onChange(choices.map((c, j) => (j === i ? value : c)));
  const optionOf = (i: number) => quotation.lines[i].options.find(o => o.productId === choices[i]?.productId);
  const subtotal = choices.reduce((n, c, i) => n + (c && optionOf(i) ? optionOf(i)!.price * c.quantity : 0), 0);
  const c = quotation.customer;
  return <View style={styles.box}>
    <Text style={[styles.caption, align]}>{[c.contactPerson, c.phone, c.city].filter(Boolean).join(' · ')}</Text>
    {quotation.lines.map((line, i) => {
      const choice = choices[i], selected = optionOf(i);
      return <View key={i} style={[styles.line, !choice && { opacity: 0.55 }]}>
        <View style={[row, styles.head]}>
          <Text style={[styles.asked, align, { flex: 1 }]} numberOfLines={2}>{line.text}{line.unit ? ` · ${line.unit}` : ''}</Text>
          {line.status !== 'matched' && <Text style={[styles.badge, line.status === 'missing' ? styles.badMissing : styles.badCheck]}>
            {t(line.status === 'missing' ? 'workspaceAssistant.quotation.missing' : 'workspaceAssistant.quotation.check')}</Text>}
          {editable && !!line.options.length && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t(choice ? 'workspaceAssistant.quotation.remove' : 'workspaceAssistant.quotation.restore')}
            onPress={() => set(i, choice ? null : { productId: (line.selected || line.options[0]).productId, quantity: line.quantity })} style={styles.icon}>
            <Ionicons name={choice ? 'close-circle-outline' : 'add-circle-outline'} size={18} color={colors.text.secondary as string} />
          </TouchableOpacity>}
        </View>
        {!!choice && <>
          {editable && line.options.length > 1
            ? <View style={[row, styles.options]}>{line.options.map(o => <TouchableOpacity key={o.productId} accessibilityRole="button" onPress={() => set(i, { ...choice, productId: o.productId })}
                style={[styles.option, o.productId === choice.productId && styles.optionOn]}>
                <Text style={[styles.optionText, o.productId === choice.productId && styles.optionTextOn]} numberOfLines={2}>{o.name} · {money(o.price)}</Text>
              </TouchableOpacity>)}</View>
            : selected && <Text style={[styles.body, align]}>{selected.name}</Text>}
          {selected && <View style={[row, styles.head]}>
            {editable ? <TextInput value={String(choice.quantity)} keyboardType="numeric" accessibilityLabel={t('workspaceAssistant.quotation.quantity')}
              onChangeText={v => { const n = Number(v.replace(/[^\d.]/g, '')); set(i, { ...choice, quantity: Number.isFinite(n) && n > 0 ? n : 1 }); }} style={styles.qty} />
              : <Text style={styles.body}>{choice.quantity}</Text>}
            <Text style={styles.caption}>× {money(selected.price)}</Text>
            <Text style={[styles.body, { flex: 1, textAlign: isRTL ? 'left' : 'right' }]}>{money(selected.price * choice.quantity)} {selected.currency || quotation.currency || ''}</Text>
          </View>}
          {!!selected?.minimum && choice.quantity < selected.minimum && <Text style={[styles.warn, align]}>{t('workspaceAssistant.quotation.minimum', { count: selected.minimum })}</Text>}
        </>}
      </View>;
    })}
    <View style={[row, styles.head]}>
      <Text style={[styles.title, { flex: 1 }, align]}>{t('workspaceAssistant.quotation.subtotal')}</Text>
      <Text style={styles.title}>{money(subtotal)} {quotation.currency || ''}</Text>
    </View>
    <Text style={[styles.caption, align]}>{t('workspaceAssistant.quotation.taxNote')}</Text>
  </View>;
}

const getStyles = () => StyleSheet.create({
  box: { gap: spacing.sm, padding: spacing.sm, backgroundColor: colors.background.surface, borderRadius: 8 },
  line: { gap: 6, paddingBottom: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  head: { alignItems: 'center', gap: 8 },
  asked: { color: colors.text.secondary, fontSize: 12.5, fontWeight: '600' },
  title: { color: colors.text.primary, fontSize: 14, fontWeight: '600' }, body: { color: colors.text.primary, fontSize: 13 },
  caption: { color: colors.text.secondary, fontSize: 12 }, warn: { color: '#b45309', fontSize: 12 },
  badge: { fontSize: 11, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, overflow: 'hidden' },
  badCheck: { color: '#b45309', backgroundColor: '#fef3c7' }, badMissing: { color: '#b91c1c', backgroundColor: '#fee2e2' },
  icon: { padding: 2 },
  options: { flexWrap: 'wrap', gap: 6 },
  option: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: colors.border, maxWidth: '100%' },
  optionOn: { borderColor: colors.primary, backgroundColor: colors.background.secondary },
  optionText: { color: colors.text.secondary, fontSize: 12.5 }, optionTextOn: { color: colors.primary, fontWeight: '600' },
  qty: { width: 64, minHeight: 34, borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: 8, color: colors.text.primary, textAlign: 'center' },
});
